import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Logger Service
 *
 * Provides logging functionality that is disabled in production builds.
 * Use this instead of console.log/error/warn to prevent exposing sensitive data in production.
 */
@Injectable({
  providedIn: 'root'
})
export class LoggerService {

  /**
   * Log a message (only in development)
   */
  log(...args: any[]): void {
    if (!environment.production) {
      console.log(...args);
    }
  }

  /**
   * Log an error (always logged, but in production could be sent to error tracking service)
   */
  error(...args: any[]): void {
    if (!environment.production) {
      console.error(...args);
    } else {
      // In production, you could send errors to a logging service like Sentry
      // this.sendToErrorTracking(args);
    }
  }

  /**
   * Log a warning (only in development)
   */
  warn(...args: any[]): void {
    if (!environment.production) {
      console.warn(...args);
    }
  }

  /**
   * Log debug information (only in development)
   */
  debug(...args: any[]): void {
    if (!environment.production) {
      console.debug(...args);
    }
  }
}

/**
 * RxJS Operator: Log observable values in development only
 *
 * Usage:
 *   this.http.get(url)
 *     .pipe(
 *       logInDevelopment('API Response'),
 *       map(...)
 *     )
 *
 * @param label Optional label to identify the log
 */
export function logInDevelopment<T>(label?: string) {
  return (source: Observable<T>): Observable<T> => {
    if (environment.production) {
      return source; // No-op in production
    }

    return source.pipe(
      tap(value => {
        if (label) {
          console.log(`[${label}]`, value);
        } else {
          console.log(value);
        }
      })
    );
  };
}
